import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileAttachments } from "@/components/ui/file-attachments";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials, fmtTime } from "@/lib/helpers";
import { UserCircle, Users, PlayCircle, CheckCircle, ArrowUpRight, Paperclip, StopCircle } from "@phosphor-icons/react";

// iter-66 — Three-bucket therapist board (Busy / Assigned / Free)
//
// The `/queue/therapists` endpoint now returns an explicit `state` field
// derived from live session + queue counts (see queue_routes.py):
//   busy      → sessions.count(therapist_id, date=today, in_progress) > 0
//   assigned  → queue_students.count(assigned) > 0 AND no in_progress
//   free      → checked in, nothing running, nothing assigned
//
// iter-66c — Busy cards now render a LIVE elapsed timer computed from
// `current_session.started_at - paused_seconds` and tick every second.
// The whole card is click-through to /sessions/<id>. After a session
// completes it's removed from `current_session`; the card falls back
// to `last_completed_session` and shows a "Last: OT · 42m" chip with
// the same duration value the Session Log renders.
const STATE_META = {
  busy: {
    label: "Busy",
    badge: "bg-teal-100 text-teal-800 border-teal-200",
    cardBorder: "border-teal-200 bg-teal-50/40",
    dot: "bg-teal-500",
  },
  assigned: {
    label: "Assigned",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    cardBorder: "border-amber-200 bg-amber-50/40",
    dot: "bg-amber-500",
  },
  free: {
    label: "Free",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    cardBorder: "border-emerald-100 bg-emerald-50/30",
    dot: "bg-emerald-500",
  },
};

const BUCKET_ORDER = ["busy", "assigned", "free"];

// Compact human-friendly duration formatter — mirrors the same helper
// used on the Attendance / Queue completed badges so a therapist card
// and a session log row read the same way.
function fmtDuration(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  if (m === 0) return "0m";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

// Live elapsed hook — recomputes every `intervalMs` (default 1s) from
// startedAt (ISO) minus already-accumulated pausedSeconds. Returns
// elapsed seconds. When startedAt is missing we return 0.
function useLiveElapsed(startedAt, pausedSeconds = 0, pausedAt = null, tickMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [startedAt, tickMs]);

  if (!startedAt) return 0;
  try {
    const startMs = new Date(startedAt).getTime();
    // If the session is currently paused, freeze the timer at
    // paused_at (i.e. don't accumulate seconds while paused).
    const referenceMs = pausedAt ? new Date(pausedAt).getTime() : now;
    const rawElapsed = Math.floor((referenceMs - startMs) / 1000);
    return Math.max(0, rawElapsed - (Number(pausedSeconds) || 0));
  } catch {
    return 0;
  }
}

export default function TherapistQueuePage() {
  const { activeBranch } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const branchId = activeBranch === "all" ? null : activeBranch;
  const navigate = useNavigate();
  // Individual student panel for a running group session:
  // { session, student }.
  const [studentPanel, setStudentPanel] = useState(null);
  const [studentNotes, setStudentNotes] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/queue/therapists", { params: branchId ? { branch_id: branchId } : {} });
        if (mounted) setRows(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { if (mounted) setLoading(false); }
    };
    setLoading(refreshKey === 0);
    load();
    // Refresh every 20s so a completed session flips the card from
    // Busy → Free (with last-completed chip) without a page reload.
    const id = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(id); };
  }, [activeBranch, refreshKey]);

  const completeStudent = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post(`/sessions/${studentPanel.session.id}/complete-student`, {
        student_id: studentPanel.student.id,
        notes: studentNotes,
      });
      toast.success(data.group_completed
        ? `${studentPanel.student.name} completed — that was the last student, so the session is finished`
        : `${studentPanel.student.name} completed · ${data.remaining_students} still in session`);
      setStudentPanel(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFinishing(false);
    }
  };

  // Group rows into the three buckets. Backend already sorts busy →
  // assigned → free by name, so we just partition here.
  const buckets = useMemo(() => {
    const b = { busy: [], assigned: [], free: [] };
    for (const r of rows) {
      const s = r.state || (r.status === "in_session" ? "busy" : "free");
      (b[s] || b.free).push(r);
    }
    return b;
  }, [rows]);

  const totalBusy = buckets.busy.length;
  const totalAssigned = buckets.assigned.length;
  const totalFree = buckets.free.length;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-1.5">Live board</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Therapist Queue</h1>
        <p className="text-sm text-muted-foreground mt-1">Who is on the floor and what they&rsquo;re doing right now.</p>
      </div>

      {/* Top summary strip — mirrors the counts on the Student Queue
          page so admins can spot a mismatch at a glance. */}
      <div className="grid grid-cols-3 gap-3" data-testid="therapist-queue-summary">
        <SummaryPill state="busy" count={totalBusy} testId="summary-busy" />
        <SummaryPill state="assigned" count={totalAssigned} testId="summary-assigned" />
        <SummaryPill state="free" count={totalFree} testId="summary-free" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading&hellip;</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            No staff checked in today. Head to <b>Attendance</b> to check them in.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {BUCKET_ORDER.map((bucket) => {
            const list = buckets[bucket] || [];
            if (list.length === 0) return null;
            const meta = STATE_META[bucket];
            return (
              <section key={bucket} className="space-y-3" data-testid={`therapist-bucket-${bucket}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                  <h2 className="font-display text-lg font-semibold tracking-tight">
                    {meta.label}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({list.length})
                    </span>
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
                  {list.map((r) => (
                    <TherapistCard
                      key={r.therapist_id}
                      r={r}
                      state={bucket}
                      meta={meta}
                      onOpenSession={(sid) => navigate(`/sessions/${sid}`)}
                      onOpenStudent={(cs, s) => { setStudentNotes(""); setStudentPanel({ session: cs, student: s }); }}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Individual student panel for a group session — notes, files and
          completing just this student while the rest carry on. */}
      <Dialog open={!!studentPanel} onOpenChange={(o) => !o && setStudentPanel(null)}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {studentPanel?.student?.name}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Notes and files saved here belong to {studentPanel?.student?.name} alone.
              Completing finishes only their session — everyone else carries on.
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Session notes for {studentPanel?.student?.name}</Label>
              <Textarea
                rows={5}
                value={studentNotes}
                onChange={e => setStudentNotes(e.target.value)}
                placeholder="What was covered with this student today?"
                data-testid="tq-student-notes-textarea"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Paperclip size={14} weight="duotone" /> Attachments for {studentPanel?.student?.name}
              </Label>
              {studentPanel && (
                <FileAttachments
                  entityType="session_student"
                  entityId={`${studentPanel.session.id}::${studentPanel.student.id}`}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStudentPanel(null)} data-testid="tq-student-panel-cancel">
              Cancel
            </Button>
            <Button
              onClick={completeStudent}
              disabled={finishing}
              className="gap-1.5"
              data-testid="tq-student-panel-complete"
            >
              <StopCircle size={14} weight="fill" />
              {finishing ? "Completing…" : "Complete student"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TherapistCard({ r, state, meta, onOpenSession, onOpenStudent }) {
  const inProgress = r.in_progress_count ?? (state === "busy" ? 1 : 0);
  const assigned = r.assigned_count ?? 0;
  // iter-66d — Support multiple parallel sessions (e.g. Start-All from
  // the Assigned column dispatches 3 separate sessions to the same
  // therapist). Render one live strip per session, each linking to
  // its own /sessions/<id>. Falls back to the singular
  // `current_session` field for older payloads.
  const currentSessions = r.current_sessions && r.current_sessions.length
    ? r.current_sessions
    : (r.current_session ? [r.current_session] : []);
  const last = r.last_completed_session;
  const hasCurrent = currentSessions.length > 0;

  return (
    <Card
      className={`hover:shadow-md transition-all ${meta.cardBorder}`}
      data-testid={`therapist-card-${r.therapist_id}`}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(r.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{r.name}</div>
            <div className="text-xs text-muted-foreground truncate">{r.email}</div>
          </div>
          <Badge className={meta.badge} data-testid={`therapist-state-${r.therapist_id}`}>
            {meta.label}
          </Badge>
        </div>

        {/* Live session strips — one per running session. Each is its
            own clickable link to /sessions/<id>. */}
        {hasCurrent && (
          <div className="space-y-1.5">
            {currentSessions.map((cs) => (
              <LiveSessionStrip
                key={cs.id}
                cs={cs}
                onOpen={() => onOpenSession?.(cs.id)}
                onOpenStudent={onOpenStudent}
                testId={`therapist-live-${r.therapist_id}-${cs.id}`}
              />
            ))}
          </div>
        )}

        {/* Last-completed chip — only when idle AND there was a session
            earlier today. Duration is server-computed to match the
            Session Log's total-time column exactly. */}
        {!hasCurrent && last && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenSession?.(last.id); }}
            className="w-full text-left flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-2.5 py-2 hover:bg-emerald-50 transition-colors"
            data-testid={`therapist-last-${r.therapist_id}`}
            title="Open the last completed session"
          >
            <span
              className="inline-flex h-5 shrink-0 items-center justify-center rounded-md text-white text-[10px] font-bold px-1.5"
              style={{ backgroundColor: last.therapy_color || "#4A6753" }}
            >
              {last.therapy_code || "SES"}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last session</div>
              <div className="text-xs font-medium truncate flex items-center gap-1">
                <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" />
                {last.student_name || "Session"} &middot; {fmtDuration(Math.round((last.duration_seconds || 0) / 60))}
              </div>
            </div>
            <ArrowUpRight size={14} weight="bold" className="text-emerald-600 shrink-0" />
          </button>
        )}

        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border">
          <Stat
            label="In session"
            value={inProgress}
            icon={<PlayCircle size={12} weight="duotone" className="text-teal-600" />}
          />
          <Stat
            label="Assigned"
            value={assigned}
            icon={<Users size={12} weight="duotone" className="text-amber-600" />}
          />
          <Stat
            label="Since"
            value={r.checked_in_at ? fmtTime(r.checked_in_at) : "\u2014"}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// One clickable strip per running session — with its own live timer.
// Rendered by TherapistCard for every entry in `current_sessions`.
function LiveSessionStrip({ cs, onOpen, onOpenStudent, testId }) {
  const liveSeconds = useLiveElapsed(
    cs?.started_at,
    cs?.paused_seconds || 0,
    cs?.paused_at || null,
  );
  const liveMins = Math.floor(liveSeconds / 60);
  const isPaused = Boolean(cs?.paused_at);
  // Group sessions (more than one student) list every student below the
  // strip. Single-student strips are left exactly as they were.
  const students = cs?.students || [];
  const isGroup = students.length > 1;
  return (
    <div className="space-y-1.5">
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left flex items-center gap-2 rounded-md border border-teal-200 bg-white px-2.5 py-2 hover:bg-teal-50 transition-colors"
      data-testid={testId}
      title={`Open ${cs.student_name || "session"} — ${cs.therapy_name || cs.therapy_code || ""}`}
    >
      <span
        className="inline-flex h-5 shrink-0 items-center justify-center rounded-md text-white text-[10px] font-bold px-1.5"
        style={{ backgroundColor: cs.therapy_color || "#4A6753" }}
      >
        {cs.therapy_code || "SES"}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-foreground truncate">
          {isGroup
            ? `${cs.student_count} students`
            : (cs.student_name || "Session")}
        </div>
        <div className="text-[11px] font-semibold tabular-nums text-teal-700 flex items-center gap-1">
          {isPaused ? (
            <>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {fmtDuration(liveMins)} · paused
            </>
          ) : (
            <>
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-70 pulse-dot" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-500" />
              </span>
              {fmtDuration(liveMins)} in session
            </>
          )}
        </div>
      </div>
      <ArrowUpRight size={14} weight="bold" className="text-teal-600 shrink-0" />
    </button>
    {isGroup && (
      <div
        className="rounded-md border border-teal-100 bg-white/70 px-2.5 py-2 space-y-1"
        data-testid={`group-students-${cs.id}`}
      >
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Students in session
        </div>
        {students.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenStudent?.(cs, s); }}
            className="w-full text-left text-xs font-medium text-teal-800 underline underline-offset-2 hover:text-teal-900 truncate"
            data-testid={`group-student-${cs.id}-${s.id}`}
            title={`Open ${s.name}'s session`}
          >
            {s.name}
          </button>
        ))}
      </div>
    )}
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="text-center py-2">
      <div className="overline text-[9px]">{label}</div>
      <div className="text-sm font-semibold mt-0.5 flex items-center justify-center gap-1">
        {icon}
        {value}
      </div>
    </div>
  );
}

function SummaryPill({ state, count, testId }) {
  const meta = STATE_META[state];
  return (
    <div
      className={`rounded-lg border ${meta.cardBorder} px-3 py-2.5 flex items-center gap-2.5`}
      data-testid={testId}
    >
      <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{meta.label}</div>
        <div className="text-xl font-display font-bold leading-tight">{count}</div>
      </div>
      <UserCircle size={20} weight="duotone" className="text-muted-foreground opacity-70" />
    </div>
  );
}
